import { Truck, Stethoscope, Users, Ambulance, Droplet, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BOOKING_URL } from "@/lib/api";

const SERVICES = [
  {
    id: "medical_courier",
    icon: Truck,
    title: "Medical Courier Services",
    description:
      "Secure, timely, chain-of-custody transport of specimens, pharmaceuticals, and medical items between facilities.",
  },
  {
    id: "clinical_assistance",
    icon: Stethoscope,
    title: "Clinical Assistance",
    description:
      "Hands-on operational support for clinical teams — optimizing workflows, intake, and patient-facing coordination.",
  },
  {
    id: "medical_staffing",
    icon: Users,
    title: "Medical Staffing Support",
    description:
      "Qualified, vetted healthcare professionals placed quickly to cover gaps and scale your team with confidence.",
  },
  {
    id: "non_emergency_transport",
    icon: Ambulance,
    title: "Non-Emergency Patient Transportation",
    description:
      "Safe, respectful, and on-time transportation for patients to and from appointments, dialysis, and discharges.",
  },
  {
    id: "phlebotomy",
    icon: Droplet,
    title: "Phlebotomy Services",
    description:
      "Professional, on-site and mobile blood-draw services performed by certified phlebotomists — efficient and patient-friendly.",
  },
];

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="bg-slate-50 border-t border-slate-200 py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-12">
          <div className="lg:col-span-8">
            <div className="text-[11px] uppercase tracking-[0.22em] text-brand-teal flex items-center gap-2 mb-5">
              <span className="h-px w-8 bg-brand-gold" /> Our Services
            </div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-900 tracking-tight leading-[1.1]">
              Five essential services. One dependable partner.
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-slate-600 leading-relaxed">
              Purpose-built healthcare support — engineered for reliability,
              measured on outcomes, delivered with care. Book any service on
              our booking site.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-l border-t border-slate-200 bg-white" data-testid="services-grid">
          {SERVICES.map((service, idx) => {
            const Icon = service.icon;
            const number = idx + 1;
            return (
              <article
                key={service.id}
                data-testid={`service-card-${service.id}`}
                className="relative border-r border-b border-slate-200 p-8 md:p-10"
              >
                <div className="flex items-start justify-between">
                  <span className="inline-flex h-12 w-12 items-center justify-center bg-slate-900 text-white rounded-sm">
                    <Icon className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.22em] text-slate-400 font-mono">
                    0{number}
                  </span>
                </div>
                <h3 className="mt-8 font-heading text-xl md:text-2xl font-semibold text-slate-900 tracking-tight">
                  {service.title}
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  {service.description}
                </p>
              </article>
            );
          })}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-4 border border-slate-200 bg-brand-navy text-white p-6 md:p-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-amber-300 mb-1">
              Ready to schedule?
            </div>
            <h3 className="font-heading text-xl md:text-2xl font-semibold tracking-tight">
              Book any service in minutes on our booking portal.
            </h3>
          </div>
          <Button
            asChild
            data-testid="services-book-cta-button"
            size="lg"
            className="bg-brand-teal hover:bg-brand-teal-hover text-white rounded-sm h-12 px-6 font-semibold tracking-wide whitespace-nowrap"
          >
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              Book a Service <ExternalLink className="h-4 w-4 ml-2" />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
}
